import events from "add-event-listener";
import * as React from "react";
import { DependencyList } from "react";

import { noop } from "../../../../../Common/TypingUtils";

export function useWindowResize(callback: () => void, deps: React.DependencyList): void {
    const wrappedCallback = React.useCallback(callback, deps);
    React.useEffect(() => {
        events.addEventListener(window, "resize", wrappedCallback);
        return () => {
            events.removeEventListener(window, "resize", wrappedCallback);
        };
    }, [wrappedCallback]);
}

export function useDimensions(
    { liveMeasure = true }: { liveMeasure?: boolean } = {},
    deps?: DependencyList
): [(node: undefined | null | HTMLElement) => void, undefined | DOMRect, undefined | HTMLElement] {
    const [dimensions, setDimensions] = React.useState<DOMRect>();
    const [node, setNode] = React.useState<HTMLElement>();

    const ref = React.useCallback((x: null | undefined | HTMLElement) => {
        setNode(x ?? undefined);
    }, []);

    React.useLayoutEffect(() => {
        if (node) {
            const measure = () => window.requestAnimationFrame(() => setDimensions(node.getBoundingClientRect()));
            measure();
            if (liveMeasure) {
                window.addEventListener("resize", measure);
                window.addEventListener("scroll", measure);

                return () => {
                    window.removeEventListener("resize", measure);
                    window.removeEventListener("scroll", measure);
                };
            }
        }
        return noop;
    }, [node, ...(deps ?? [])]);

    return [ref, dimensions, node];
}

export function useDetectTextOverflowed(
    { liveMeasure = true }: { liveMeasure?: boolean } = {},
    deps?: DependencyList
): [(node: undefined | null | HTMLElement) => void, undefined | boolean] {
    const [hasTextOverflow, setHasTextOverflow] = React.useState<boolean>();
    const [node, setNode] = React.useState<HTMLElement>();

    const ref = React.useCallback((x: null | undefined | HTMLElement) => {
        setNode(x ?? undefined);
    }, []);

    React.useLayoutEffect(() => {
        if (node) {
            const measure = () =>
                window.requestAnimationFrame(() => {
                    setHasTextOverflow(node.scrollWidth > node.offsetWidth);
                });
            measure();
            if (liveMeasure) {
                window.addEventListener("resize", measure);
                window.addEventListener("scroll", measure);

                return () => {
                    window.removeEventListener("resize", measure);
                    window.removeEventListener("scroll", measure);
                };
            }
        }
        return noop;
    }, [node, ...(deps ?? [])]);

    return [ref, hasTextOverflow];
}
